import React, { useMemo } from 'react';
import _ from 'underscore';

import { FreecodeTabContentContainer, Title, FormSection, Label } from '../../styles';
import { SubHeader, CodeContainer } from './styles';
import { useAppContext, useFreecodeContext } from 'hooks/useContext';

export default function FreecodePreview() {
  const { isDarkMode } = useAppContext();
  const { testImpl, languageData } = useFreecodeContext();

  const renderCodeSnippet = (source) => {
    return (
      <CodeContainer isDarkMode={isDarkMode}>
        <code>{source}</code>
      </CodeContainer>
    );
  };

  const values = useMemo(() => {
    if (!testImpl?.generated) return {};
    return {
      skeletons: _.map(testImpl.generated.skeletons, (val, key) => ({
        name: languageData?.[key]?.SimpleName || key,
        code: val,
      })),
      apiStrings: {
        challenge: JSON.stringify(testImpl.generated.apiStrings?.challenge, null, 2),
        challengeDescription: JSON.stringify(testImpl.generated.apiStrings?.challengeDescription, null, 2),
        solution: JSON.stringify(testImpl.generated.apiStrings.solution, null, 2),
      },
      autocompletions: JSON.stringify(testImpl.generated.autocompletions, null, 2),
      allLevelNames: JSON.stringify(testImpl.generated.allLevelNames, null, 2),
    };
  }, [testImpl, languageData]);

  return (
    <FreecodeTabContentContainer isDarkMode={isDarkMode}>
      <FormSection paddingX>
        <Label>The contents of this tab have been automatically generated and can not be manually edited.</Label>
      </FormSection>
      {values.skeletons && (
        <FormSection paddingX>
          <Title>Skeletons</Title>
          <Label>These are the boilerplate code snippets a user starts with when creating a solution.</Label>
          {values.skeletons.map((element, index) => (
            <React.Fragment key={index}>
              <SubHeader>{element.name}</SubHeader>
              {renderCodeSnippet(element.code)}
            </React.Fragment>
          ))}
        </FormSection>
      )}
      {values.apiStrings && (
        <FormSection paddingX>
          <Title>API Strings</Title>
          <Label>Function display names in different languages for use in the API description.</Label>
          <SubHeader>Challenge API names</SubHeader>
          {renderCodeSnippet(values.apiStrings.challenge, 'json')}
          <SubHeader>Challenge API generated description</SubHeader>
          {renderCodeSnippet(values.apiStrings.challengeDescription, 'json')}
          <SubHeader>Solution API names</SubHeader>
          {renderCodeSnippet(values.apiStrings.solution, 'json')}
        </FormSection>
      )}
      {values.autocompletions && (
        <FormSection paddingX>
          <Title>Generated auto completion values</Title>
          <Label>Additional auto completion values sent by the executor.</Label>
          {renderCodeSnippet(values.autocompletions, 'json')}
        </FormSection>
      )}
      {values.allLevelNames && (
        <FormSection paddingX>
          <Title>Generated level names</Title>
          <Label>The level names returned from the executor.</Label>
          {renderCodeSnippet(values.allLevelNames, 'json')}
        </FormSection>
      )}
    </FreecodeTabContentContainer>
  );
}
